import React from 'react'
import { Link } from 'react-router-dom';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import send from '../assets/img/svg/send.svg'
import map from '../assets/img/svg/map-1.svg'
import moon from '../assets/img/svg/moon.svg'
import verified from '../assets/img/svg/verified.svg'
import user from '../assets/img/svg/user-1.svg'
import { getImageUrl } from '../utils/api';

const settings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3000,
  arrows: false,
};

interface DBProperty {
  _id: string; title: string; price: number; status: string; type: string;
  city?: string; address?: string; bedrooms?: number; bathrooms?: number; area?: string;
  images?: string[]; isActive: boolean; isFeatured?: boolean; createdAt: string;
  owner?: { name: string; role: string };
}

export default function GridProperty({ item, border }: { item: DBProperty, border: boolean }) {
  const propertyImages = item.images && item.images.length > 0 ? item.images : ['/assets/img/p-1.jpg'];

  return (
    <div className={`group bg-white rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${border ? 'border border-gray-100' : 'shadow-sm'}`}>
      {/* Image Section */}
      <div className="relative p-3">
        <div className="relative rounded-2xl overflow-hidden">
          {/* Status Badges */}
          <div className="absolute top-4 left-4 z-10 flex flex-wrap gap-2">
            {item.isFeatured && (
              <div className="bg-purple-600/90 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg flex items-center shadow-lg">
                <img src={user} alt="" className="w-3 h-3 me-1.5 brightness-0 invert" /> Featured
              </div>
            )}
            <div className="bg-green-600/90 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg flex items-center shadow-lg">
              <img src={verified} alt="" className="w-3 h-3 me-1.5 brightness-0 invert" /> Verified
            </div>
          </div>

          {/* Slider */}
          <div className="relative aspect-[4/3]">
            <Slider {...settings} className="property-slider-inner h-full">
              {propertyImages.map((el: any, index: number) => (
                <div key={index} className="h-full">
                  <Link to={`/property-details/${item._id}`}>
                    <img 
                      src={getImageUrl(el)} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                      alt={item.title} 
                    />
                  </Link>
                </div>
              ))}
            </Slider>
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="px-6 pb-6 mt-2">
        <div className="flex items-center gap-2 mb-4">
          {item.status === "For Rent" && (
            <span className="bg-green-50 text-green-600 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-green-100">
              For Rent
            </span>
          )}
          {item.status === "For Sale" && (
            <span className="bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-red-100">
              For Sale
            </span>
          )}
          <span className="bg-purple-50 text-purple-600 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-purple-100">
            {item.type}
          </span>
        </div>

        <Link to={`/property-details/${item._id}`} className="block">
          <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors line-clamp-1">
            {item.title}
          </h4>
        </Link>

        <div className="flex items-center text-gray-400 text-sm mb-6">
          <img src={map} alt="" className="w-4 h-4 me-2 opacity-50" />
          <span className="line-clamp-1">{item.city || item.address || 'Location'}</span>
        </div>

        {/* Features Strip */}
        <div className="flex items-center justify-between py-5 border-t border-gray-50">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center mb-2 group-hover:bg-primary/10 transition-colors">
              <i className="fa-solid fa-bed text-gray-400 group-hover:text-primary transition-colors text-xs"></i>
            </div>
            <span className="text-[10px] font-bold text-gray-900 uppercase tracking-tight">{item.bedrooms || 0} Beds</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center mb-2 group-hover:bg-primary/10 transition-colors">
              <i className="fa-solid fa-shower text-gray-400 group-hover:text-primary transition-colors text-xs"></i>
            </div>
            <span className="text-[10px] font-bold text-gray-900 uppercase tracking-tight">{item.bathrooms || 0} Baths</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center mb-2 group-hover:bg-primary/10 transition-colors">
              <i className="fa-solid fa-maximize text-gray-400 group-hover:text-primary transition-colors text-xs"></i>
            </div>
            <span className="text-[10px] font-bold text-gray-900 uppercase tracking-tight">{item.area || 0} sqft</span>
          </div>
        </div>

        {/* Footer Area */}
        <div className="flex items-center justify-between pt-5 border-t border-gray-50">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Starting Price</p>
            <h6 className="text-xl font-black text-primary tracking-tight">₹{item.price?.toLocaleString()}</h6>
          </div>
          <Link 
            to={`/property-details/${item._id}`} 
            className="w-12 h-12 rounded-2xl bg-primary-dark text-white flex items-center justify-center shadow-lg shadow-primary-dark/20 hover:bg-primary hover:-translate-y-1 transition-all duration-300"
          >
            <i className="fa-solid fa-arrow-right-long"></i>
          </Link>
        </div>
      </div>
    </div>
  )
}

